import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Divider from 'src/components/ui/Divider/Divider';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ReturnsCreateShell } from './ReturnsCreateTracking';
import { ActionBar, ReturnsConfiguratorForm, Section } from './ReturnsConfiguratorForm';
import {
  returnFormTrackingStepSchema,
  returnFormProductsStepSchema,
  ReturnFormProductsStepValues,
  useReturnsFormContext,
} from './ReturnsFormContext';

const ReturnsCreateProducts: React.FC = () => {
  const { t } = useTranslation();
  const { formState } = useReturnsFormContext();
  const formRef = useRef<{ triggerSubmit: () => void }>(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [disableNextButton, setDisableNextButton] = useState(true);

  const { handleSubmit } = useForm<ReturnFormProductsStepValues>({
    resolver: zodResolver(returnFormProductsStepSchema(t)),
    defaultValues: formState.products,
  });

  const navigate = useNavigate();

  const navigateToSummary = () => {
    document.querySelector('.main-content-wrapper')?.scrollTo({ top: 0, behavior: 'smooth' });
    navigate('/returns/create/summary');
  };

  useEffect(() => {
    if (formState) {
      const previousStepData = formState.tracking;
      const { success } = returnFormTrackingStepSchema(t).safeParse(previousStepData);
      if (!success) {
        navigate('/returns/create/tracking');
      }
    }
  }, [formState]);

  useEffect(() => {
    if (formSubmitted) {
      const validationResult = returnFormProductsStepSchema(t).safeParse(formState.products);
      if (validationResult.success) {
        setFormSubmitted(false);
        navigateToSummary();
      }
    }
  }, [formState.products, formSubmitted]);

  const submit = () => {
    const validationResult = returnFormProductsStepSchema(t).safeParse(formState.products);

    if (validationResult.success) {
      navigateToSummary();
    } else {
      setFormSubmitted(true);
      if (formRef.current) {
        formRef.current.triggerSubmit();
      }
    }
  };

  return (
    <ReturnsCreateShell step={2} title={t('navbar.returns')} headline={t('navbar.create_return')}>
      <Section className="flex flex-col gap-y-32">
        <ReturnsConfiguratorForm
          ref={formRef}
          onSubmit={handleSubmit(submit)}
          step={2}
          setDisableNextButton={setDisableNextButton}
        />
        <Divider className="" />
        <div className="flex justify-end gap-12">
          <ActionBar>
            <Button
              title={t('general.cancel')}
              tone={ButtonToneProps.SECONDARY}
              onClick={() => {
                navigate('/returns');
              }}
            />
            <Button
              title={t('general.continue')}
              onClick={submit}
              tone={ButtonToneProps.PRIMARY_BLUE}
              disabled={disableNextButton}
            />
          </ActionBar>
        </div>
      </Section>
    </ReturnsCreateShell>
  );
};

export default ReturnsCreateProducts;
